/**
 * ArtifactRubric
 * --------------
 * Phase B3 rubric-scoring UI for a single portfolio artifact. Renders inside
 * the ArtifactCard: a "Score with AI" trigger plus, once scored, a compact
 * per-dimension breakdown (score + feedback) and an overall verdict.
 *
 * Data flow:
 *   - The score lives server-side on the artifact (rubric_score /
 *     rubric_feedback / last_scored_at), populated by POST /:id/score.
 *   - The trigger fires `scorePortfolioArtifact` via React Query; on success we
 *     invalidate the artifacts list so the persisted result re-renders from
 *     fresh server data (same pattern as the site-check button in ArtifactCard).
 *
 * The component reads the PERSISTED shape off the artifact (rubric_score grid +
 * rubric_feedback narrative) so the rubric survives a reload — it does not rely
 * on the in-memory POST response. Both JSONB columns are typed loosely on the
 * API model (Record<string, unknown>), so we narrow defensively here and render
 * nothing if the shape is unexpected (e.g. a legacy/partial row).
 *
 * Presentational + self-contained mutation, mirroring ArtifactCard's check-site
 * button. The parent passes only the artifact.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  Collapse,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useState } from "react";
import { PortfolioArtifact, scorePortfolioArtifact } from "../../api/client";

const SCORE_MAX = 5;

const EVIDENCE_LABEL_KEYS: Record<string, string> = {
  repo: "ArtifactRubric.evidenceLabelRepo",
  non_github: "ArtifactRubric.evidenceLabelNonGithub",
  not_public: "ArtifactRubric.evidenceLabelNotPublic",
  fetch_failed: "ArtifactRubric.evidenceLabelFetchFailed",
  no_url: "ArtifactRubric.evidenceLabelNoUrl",
};

interface ArtifactRubricProps {
  artifact: PortfolioArtifact;
}

/** Canonical dimension order + human labels. Mirrors the backend
 *  RUBRIC_DIMENSIONS map (app/schemas/portfolio_rubric.py). */
const DIMENSION_LABELS: Array<{ key: string; labelKey: string }> = [
  { key: "narrative_clarity", labelKey: "ArtifactRubric.dimensionLabelNarrativeClarity" },
  { key: "technical_depth", labelKey: "ArtifactRubric.dimensionLabelTechnicalDepth" },
  { key: "evidence_and_metrics", labelKey: "ArtifactRubric.dimensionLabelEvidenceAndMetrics" },
  { key: "deployment_and_reachability", labelKey: "ArtifactRubric.dimensionLabelDeploymentAndReachability" },
  { key: "recruiter_readiness", labelKey: "ArtifactRubric.dimensionLabelRecruiterReadiness" },
];

/** A 1-5 score → Chip color. Low scores read as warnings, high as success. */
function scoreColor(score: number): "error" | "warning" | "success" {
  if (score <= 2) return "error";
  if (score === 3) return "warning";
  return "success";
}

/** Narrow the persisted rubric_score JSONB into the numeric grid we render.
 *  Returns null if the shape is missing or malformed (legacy/partial rows). */
function readScoreGrid(
  raw: Record<string, unknown> | null,
): { overall: number; dimensions: Record<string, number> } | null {
  if (!raw || typeof raw !== "object") return null;
  const overall = raw.overall_score;
  const dims = raw.dimensions;
  if (typeof overall !== "number" || !dims || typeof dims !== "object") {
    return null;
  }
  return {
    overall,
    dimensions: dims as Record<string, number>,
  };
}

/** Narrow the persisted rubric_feedback JSONB into the narrative we render. */
function readFeedback(
  raw: Record<string, unknown> | null,
): { summary: string; dimensions: Record<string, string> } | null {
  if (!raw || typeof raw !== "object") return null;
  const summary = raw.summary;
  const dims = raw.dimensions;
  if (typeof summary !== "string" || !dims || typeof dims !== "object") {
    return null;
  }
  return {
    summary,
    dimensions: dims as Record<string, string>,
  };
}

export default function ArtifactRubric({ artifact }: ArtifactRubricProps) {
  const theme = useTheme();
  const { t } = useTranslation("portfolio-kit");
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);

  const scoreMutation = useMutation({
    mutationFn: (id: string) => scorePortfolioArtifact(id),
    onSuccess: () => {
      setExpanded(true);
      qc.invalidateQueries({ queryKey: ["portfolioArtifacts"] });
    },
  });

  const grid = readScoreGrid(artifact.rubric_score);
  const feedback = readFeedback(artifact.rubric_feedback);
  const hasScore = grid !== null;

  const evidenceSource =
    typeof (artifact.rubric_score as any)?.evidence_source === "string"
      ? ((artifact.rubric_score as any).evidence_source as string)
      : null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Button
          size="small"
          startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          onClick={() => scoreMutation.mutate(artifact.id)}
          disabled={scoreMutation.isPending}
          sx={{
            color: theme.palette.primary.main,
            textTransform: "none",
            fontWeight: 500,
            minWidth: 0,
            px: 1,
          }}
        >
          {scoreMutation.isPending
            ? t("ArtifactRubric.scoringButton")
            : hasScore
              ? t("ArtifactRubric.rescoreButton")
              : t("ArtifactRubric.scoreButton")}
        </Button>

        {hasScore && grid && (
          <Tooltip title={t("ArtifactRubric.overallScoreTooltip")}>
            <Chip
              label={`${grid.overall}/${SCORE_MAX}`}
              size="small"
              color={scoreColor(grid.overall)}
            />
          </Tooltip>
        )}

        {hasScore && (
          <Button
            size="small"
            onClick={() => setExpanded((v) => !v)}
            sx={{ textTransform: "none", minWidth: 0, px: 1 }}
          >
            {expanded ? t("ArtifactRubric.hideFeedback") : t("ArtifactRubric.showFeedback")}
          </Button>
        )}
      </Stack>

      {hasScore && evidenceSource && EVIDENCE_LABEL_KEYS[evidenceSource] && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {t(EVIDENCE_LABEL_KEYS[evidenceSource])}
        </Typography>
      )}

      {scoreMutation.isPending && (
        <LinearProgress
          sx={{
            mt: 1,
            borderRadius: 1,
            "& .MuiLinearProgress-bar": { backgroundColor: theme.palette.primary.main },
          }}
        />
      )}

      {scoreMutation.isError && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 0.5 }}
        >
          {t("ArtifactRubric.scoreErrorMessage")}
        </Typography>
      )}

      {hasScore && grid && (
        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              mt: 1.5,
              p: 2,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            {feedback?.summary && (
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5 }}>
                {feedback.summary}
              </Typography>
            )}

            <Stack spacing={1.25}>
              {DIMENSION_LABELS.map(({ key, labelKey }) => {
                const score = grid.dimensions[key];
                if (typeof score !== "number") return null;
                const note = feedback?.dimensions?.[key];
                return (
                  <Box key={key}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 0.25 }}
                    >
                      <Chip
                        label={`${score}/${SCORE_MAX}`}
                        size="small"
                        color={scoreColor(score)}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {t(labelKey)}
                      </Typography>
                    </Stack>
                    {note && (
                      <Typography variant="caption" color="text.secondary">
                        {note}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
